import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react'

const ToastContext = createContext(null)

// Firm-wide toast notification system.  Replaces window.alert() so the UI
// doesn't jerk to a blocking browser dialog; also carries action buttons for
// undo-style flows.
//
// Usage:
//   const toast = useToast()
//   toast.success('Saved')
//   toast.error('Server error')
//   toast.info('Copied to clipboard')
//   toast.warn('Payment date is in the future')
//   toast.custom({ title: 'Deleted', message: 'Time entry removed',
//                  actions: [{ label: 'Undo', onClick: restore }] })
//
// Auto-dismiss defaults: success/info 4s, warn 5s, error 6s, custom 8s.
// Pass `duration: 0` to require manual dismissal.

export function ToastProvider({ children }) {
  const [toasts, setToasts] = useState([])
  const timers = useRef({})

  const dismiss = useCallback((id) => {
    setToasts((prev) => prev.filter((t) => t.id !== id))
    if (timers.current[id]) {
      clearTimeout(timers.current[id])
      delete timers.current[id]
    }
  }, [])

  const push = useCallback((toast) => {
    // Unique id — Date.now alone collides when two toasts fire in the same tick
    const id = `t-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`
    const defaults = {
      success: 4000, info: 4000, warn: 5000, error: 6000, custom: 8000
    }
    const duration = toast.duration != null ? toast.duration : (defaults[toast.type] ?? 4000)
    const entry = { ...toast, id, duration }
    setToasts((prev) => [...prev, entry])
    if (duration > 0) {
      timers.current[id] = setTimeout(() => dismiss(id), duration)
    }
    return id
  }, [dismiss])

  // Clear pending timers on unmount so we don't leak
  useEffect(() => () => {
    Object.values(timers.current).forEach(clearTimeout)
    timers.current = {}
  }, [])

  const api = useMemo(() => {
    const success = (message, opts = {}) => push({ ...opts, type: 'success', message })
    const error   = (message, opts = {}) => push({ ...opts, type: 'error',   message })
    const info    = (message, opts = {}) => push({ ...opts, type: 'info',    message })
    const warn    = (message, opts = {}) => push({ ...opts, type: 'warn',    message })
    // Legacy signature — `const { showToast } = useToast(); showToast(msg, 'success')`.
    // This alias exists because ~95 call sites across 6 files were written to
    // that API (Leads / Kanban / Reports modals / Intake Forms / etc.) but the
    // original ToastContext only exposed .success/.error/.info/.warn — so every
    // toast call threw `TypeError: showToast is not a function`, and because the
    // error was inside an async handler the whole flow silently aborted before
    // the follow-on API call, producing the "save/edit anything and it doesn't
    // reflect until reload" bug the user reported (2026-07-22 midnight).  This
    // one-liner fixes it app-wide with zero call-site edits.
    const showToast = (message, type = 'info', opts = {}) => {
      const map = { success, error, info, warn }
      const fn = map[type] || info
      return fn(message, opts)
    }
    return {
      success, error, info, warn, showToast,
      custom: (opts) => push({ ...opts, type: opts.type || 'custom' }),
      dismiss,
    }
  }, [push, dismiss])

  return (
    <ToastContext.Provider value={api}>
      {children}
      <ToastContainer toasts={toasts} onDismiss={dismiss} />
    </ToastContext.Provider>
  )
}

export function useToast() {
  const ctx = useContext(ToastContext)
  if (!ctx) {
    // Fail soft — return no-op stubs so a component tree rendered outside the
    // provider (rare — usually only in tests) doesn't crash.  Console-warn so
    // the misuse is at least visible.
    if (typeof console !== 'undefined') console.warn('useToast: no ToastProvider in tree — toasts will be dropped')
    return {
      success: () => null, error: () => null, info: () => null,
      warn: () => null, custom: () => null, dismiss: () => {},
      showToast: () => null
    }
  }
  return ctx
}

function ToastContainer({ toasts, onDismiss }) {
  if (!toasts.length) return null
  return (
    <div
      className="fixed bottom-4 right-4 z-[9999] flex flex-col gap-2 pointer-events-none"
      style={{ maxWidth: 'min(24rem, calc(100vw - 2rem))' }}
      aria-live="polite"
      aria-atomic="false"
    >
      {toasts.map((t) => (
        <Toast key={t.id} toast={t} onDismiss={() => onDismiss(t.id)} />
      ))}
    </div>
  )
}

function Toast({ toast, onDismiss }) {
  const palette = {
    success: 'bg-green-600 border-green-700',
    error:   'bg-red-600 border-red-700',
    info:    'bg-blue-600 border-blue-700',
    warn:    'bg-amber-500 border-amber-600',
    custom:  'bg-gray-800 border-gray-900',
  }
  const icons = {
    success: '✓', error: '✕', info: 'ℹ', warn: '⚠', custom: ''
  }
  const cls = palette[toast.type] || palette.custom
  const icon = icons[toast.type] || ''

  return (
    <div
      className={`${cls} text-white border rounded-lg shadow-lg px-3 py-2.5 flex items-start gap-2 pointer-events-auto`}
      role={toast.type === 'error' || toast.type === 'warn' ? 'alert' : 'status'}
    >
      {icon && <span className="text-lg leading-tight shrink-0" aria-hidden="true">{icon}</span>}
      <div className="flex-1 min-w-0">
        {toast.title && <div className="font-semibold text-sm">{toast.title}</div>}
        {toast.message && (
          <div className="text-sm break-words whitespace-pre-wrap">
            {toast.message}
          </div>
        )}
        {Array.isArray(toast.actions) && toast.actions.length > 0 && (
          <div className="mt-1.5 flex flex-wrap gap-3">
            {toast.actions.map((a, i) => (
              <button
                key={i}
                onClick={() => {
                  try { a.onClick?.() } catch (e) { console.error(e) }
                  if (a.dismissOnClick !== false) onDismiss()
                }}
                className="text-xs underline hover:no-underline font-medium"
              >
                {a.label}
              </button>
            ))}
          </div>
        )}
      </div>
      <button
        onClick={onDismiss}
        className="text-white/80 hover:text-white text-base leading-none px-1 shrink-0"
        aria-label="Dismiss notification"
      >
        ✕
      </button>
    </div>
  )
}
